import React from 'react';
import { twJoin, twMerge } from 'tailwind-merge';
import Image from 'next/image';
import { RichTextBlock } from './types/RichTextBlock';
import { RichTextRendererProps } from './types/RichTextRendererProps';
import { RichTextLink } from './rich-text-link/rich-text-link';
import OptimizedImage from '../../shared/OptimizedImage';
import QuoteIcon from '../../../../../public/images/quote-icon.svg';

export function RichTextRenderer({
  blocks,
  className,
  bulletPointIcon,
  openExternalLinksInNewTab = false,
  mutedTextClassName = 'text-neutral-40',
}: Readonly<RichTextRendererProps>) {
  const json = blocks?.json;
  const tableStyles = {
    border: '1px solid #e6e6e6',
    borderCollapse: 'collapse',
    paddingBottom: '5px',
  } as React.CSSProperties;

  function renderBlock(block: RichTextBlock, key: React.Key, elementClassName?: string) {
    // Skip empty paragraphs(they mean that the RichTextBlock is empty)
    if (
      block.type === 'paragraph' &&
      block.children === undefined &&
      block.textContent === undefined
    ) {
      return null;
    }

    const renderChildren = (children: RichTextBlock[]) =>
      children.map((child, index) => renderBlock(child, `${block.type}-${index}`));

    switch (block.type) {
      case 'paragraph':
        return (
          <p key={key} className={elementClassName}>
            {block.children ? renderChildren(block.children) : block.textContent || <br />}
          </p>
        );
      case 'emphasized':
        return (
          <em className={elementClassName} key={key}>
            {block.children ? renderChildren(block.children) : block.textContent}
          </em>
        );
      case 'underlined':
        return (
          <u className={twJoin(elementClassName, 'yellow-underline')} key={key}>
            {block.children ? renderChildren(block.children) : block.textContent}
          </u>
        );
      case 'strong':
        return (
          <strong className={elementClassName} key={key}>
            {block.children ? renderChildren(block.children) : block.textContent}
          </strong>
        );
      case 'deleted':
        return (
          <del className={elementClassName} key={key}>
            {block.children ? renderChildren(block.children) : block.textContent}
          </del>
        );
      case 'quote':
        return (
          <blockquote
            className={twJoin(elementClassName, 'my-8 space-y-2 sm:mx-4 sm:my-10 lg:my-14')}
            key={key}
          >
            <Image
              src={QuoteIcon}
              alt="Quote Icon"
              width={80}
              height={80}
              className="h-10 w-auto lg:h-20"
            />
            <div className="text-xl font-medium italic leading-normal sm:text-2xl sm:leading-normal lg:text-3xl lg:leading-normal">
              {block.children ? renderChildren(block.children) : block.textContent}
            </div>
          </blockquote>
        );
      case 'link':
        return (
          <RichTextLink
            key={key}
            href={block.metadata?.href}
            rel={block.metadata?.rel}
            target={block.metadata?.target}
            className={elementClassName}
            openExternalLinksInNewTab={openExternalLinksInNewTab}
          >
            {block.children ? renderChildren(block.children) : block.textContent}
          </RichTextLink>
        );
      case 'unordered-list':
        return (
          <ul className={elementClassName} key={key} style={{ listStyle: 'inside' }}>
            {block.children
              ? block.children.map((children, idx) => renderBlock(children, idx))
              : block.textContent || <br />}
          </ul>
        );
      case 'ordered-list':
        return (
          <ol className={`${elementClassName} list-inside list-decimal`} key={key}>
            {block.children
              ? block.children.map((children, idx) => renderBlock(children, idx))
              : block.textContent || <br />}
          </ol>
        );
      case 'list-item':
        return (
          <li
            className={twJoin(elementClassName, bulletPointIcon ? 'mb-5 flex gap-2' : 'mb-2')}
            key={key}
          >
            {bulletPointIcon && (
              <OptimizedImage sizes="40px" image={bulletPointIcon} width={35} height={35} />
            )}

            <span>
              {block.children
                ? block.children.map((children, idx) => renderBlock(children, idx))
                : block.textContent || <br />}
            </span>
          </li>
        );
      case 'line-break':
        return <br className={elementClassName} key={key} />;

      case 'table':
        return (
          <table
            className={twJoin(elementClassName, 'my-2 sm:my-4')}
            key={key}
            style={{ ...tableStyles, width: '100%' }}
          >
            <tbody>
              {block.children?.map((child, index) => renderBlock(child, `table-row-${index}`))}
            </tbody>
          </table>
        );
      case 'table-row':
        return (
          <tr
            className={twJoin(elementClassName, 'even:bg-neutral-5')}
            key={key}
            style={tableStyles}
          >
            {block.children?.map((child, index) => renderBlock(child, `table-cell-${index}`))}
          </tr>
        );
      case 'table-cell':
        return (
          <td
            className={twJoin(elementClassName, 'text-base')}
            key={key}
            style={{ ...tableStyles, padding: '10px' }}
          >
            {block.children ? renderChildren(block.children) : block.textContent}
          </td>
        );
      // Not actuall code but this is used as a workaround for styling the text inline
      case 'code':
        return (
          <span className={twMerge(elementClassName, mutedTextClassName)} key={key}>
            {block.children ? renderChildren(block.children) : block.textContent}
          </span>
        );
      // not actual superscript but this is used as a workaround for setting color(mainly used for dots)
      case 'superscripted':
        return (
          <span className={twMerge(elementClassName, 'text-primary-100')} key={key}>
            {block.children ? renderChildren(block.children) : block.textContent}
          </span>
        );
      // not actual subscript but this is used as a workaround for normal underline
      case 'subscripted':
        return (
          <span className={twMerge(elementClassName, 'underline')} key={key}>
            {block.children ? renderChildren(block.children) : block.textContent}
          </span>
        );
      case 'heading1':
        return (
          <h1 className={elementClassName} key={key}>
            {block.children ? renderChildren(block.children) : block.textContent}
          </h1>
        );
      case 'heading2':
        return (
          <h2 className={elementClassName} key={key}>
            {block.children ? renderChildren(block.children) : block.textContent}
          </h2>
        );
      case 'heading3':
        return (
          <h3 className={elementClassName} key={key}>
            {block.children ? renderChildren(block.children) : block.textContent}
          </h3>
        );
      case 'heading4':
        return (
          <h4 className={elementClassName} key={key}>
            {block.children ? renderChildren(block.children) : block.textContent}
          </h4>
        );
      case 'heading5':
        return (
          <h5 className={elementClassName} key={key}>
            {block.children ? renderChildren(block.children) : block.textContent}
          </h5>
        );
      case 'heading6':
        return (
          <h6 className={elementClassName} key={key}>
            {block.children ? renderChildren(block.children) : block.textContent}
          </h6>
        );

      default:
        return (
          <span className={elementClassName} key={key}>
            {block?.textContent ?? ''}
          </span>
        );
    }
  }

  if (!json || !Array.isArray(json)) {
    return undefined;
  }

  return <>{json.map((block, index) => renderBlock(block, index, className))}</>;
}
